import { baseAPI } from '../base-api';
import type {
  Checklist,
  ChecklistItem,
  WorkRequestChecklist,
} from './types';

export const checklistAPI = baseAPI.injectEndpoints({
  endpoints: (builder) => ({
    checklists: builder.query<Checklist[], void>({
      query: () => 'checklists',
      transformResponse: (response: any) =>
        Array.isArray(response) ? response : response?.content ?? [],
      providesTags: (result) =>
        result
          ? [
              ...result.map((c) => ({ type: 'Checklists' as const, id: c.id })),
              { type: 'Checklists', id: 'list' },
            ]
          : [{ type: 'Checklists', id: 'list' }],
    }),

    checklist: builder.query<Checklist, number>({
      query: (id) => `checklists/${id}`,
      providesTags: (result) =>
        result ? [{ type: 'Checklists', id: result.id }] : [],
    }),

    createChecklist: builder.mutation<
      Checklist,
      { name: string; description?: string | null }
    >({
      query: (body) => ({ url: 'checklists', method: 'POST', body }),
      invalidatesTags: [{ type: 'Checklists', id: 'list' }],
    }),

    updateChecklist: builder.mutation<
      Checklist,
      { id: number; name: string; description?: string | null }
    >({
      query: ({ id, ...body }) => ({
        url: `checklists/${id}`,
        method: 'PUT',
        body: { id, ...body },
      }),
      invalidatesTags: (result) =>
        result
          ? [
              { type: 'Checklists', id: result.id },
              { type: 'Checklists', id: 'list' },
            ]
          : [],
    }),

    deleteChecklist: builder.mutation<void, number>({
      query: (id) => ({ url: `checklists/${id}`, method: 'DELETE' }),
      invalidatesTags: [{ type: 'Checklists', id: 'list' }],
    }),

    checklistItems: builder.query<ChecklistItem[], number>({
      query: (checklistId) => ({
        url: 'checklist-items',
        params: { checklistId },
      }),
      transformResponse: (response: any) =>
        Array.isArray(response) ? response : response?.content ?? [],
      providesTags: (result, _error, checklistId) =>
        result
          ? [
              ...result.map((i) => ({
                type: 'ChecklistItems' as const,
                id: i.id,
              })),
              { type: 'ChecklistItems', id: `list-${checklistId}` },
            ]
          : [{ type: 'ChecklistItems', id: `list-${checklistId}` }],
    }),

    createChecklistItem: builder.mutation<
      ChecklistItem,
      {
        checklistId: number;
        name: string;
        description?: string | null;
        sortOrder?: number | null;
      }
    >({
      query: (body) => ({ url: 'checklist-items', method: 'POST', body }),
      invalidatesTags: (_result, _error, arg) => [
        { type: 'ChecklistItems', id: `list-${arg.checklistId}` },
      ],
    }),

    updateChecklistItem: builder.mutation<
      ChecklistItem,
      {
        id: number;
        checklistId: number;
        name: string;
        description?: string | null;
        sortOrder?: number | null;
      }
    >({
      query: ({ id, ...body }) => ({
        url: `checklist-items/${id}`,
        method: 'PUT',
        body: { id, ...body },
      }),
      invalidatesTags: (_result, _error, arg) => [
        { type: 'ChecklistItems', id: arg.id },
        { type: 'ChecklistItems', id: `list-${arg.checklistId}` },
      ],
    }),

    deleteChecklistItem: builder.mutation<
      void,
      { id: number; checklistId: number }
    >({
      query: ({ id }) => ({ url: `checklist-items/${id}`, method: 'DELETE' }),
      invalidatesTags: (_result, _error, arg) => [
        { type: 'ChecklistItems', id: `list-${arg.checklistId}` },
      ],
    }),

    workRequestChecklists: builder.query<WorkRequestChecklist[], number>({
      query: (workRequestId) => ({
        url: 'work-request-checklists',
        params: { workRequestId },
      }),
      transformResponse: (response: any) =>
        Array.isArray(response) ? response : response?.content ?? [],
      providesTags: (result, _error, workRequestId) =>
        result
          ? [
              ...result.map((c) => ({
                type: 'WorkRequestChecklists' as const,
                id: c.id,
              })),
              {
                type: 'WorkRequestChecklists',
                id: `list-${workRequestId}`,
              },
            ]
          : [
              {
                type: 'WorkRequestChecklists',
                id: `list-${workRequestId}`,
              },
            ],
    }),
  }),
  overrideExisting: false,
});

export const {
  useChecklistsQuery,
  useChecklistQuery,
  useCreateChecklistMutation,
  useUpdateChecklistMutation,
  useDeleteChecklistMutation,
  useChecklistItemsQuery,
  useCreateChecklistItemMutation,
  useUpdateChecklistItemMutation,
  useDeleteChecklistItemMutation,
  useWorkRequestChecklistsQuery,
} = checklistAPI;
